import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';

import { ReactComponent as NoteIcon } from 'assets/images/notes.svg';
import { ReactComponent as QualifiedIcon } from 'assets/images/qualified.svg';
import { ReactComponent as UnqualifiedIcon } from 'assets/images/unqualified.svg';
import { Note, NoteTypes, Colors, CommunicationDisplay } from 'typings';
import { RedoRounded as RedoRoundedIcon } from '@material-ui/icons';

interface NoteProps extends CommunicationDisplay {
  data: Note;
  children?: React.ReactNode;
  caption?: React.ReactElement;
}

const useStyles = makeStyles((theme: Theme) => ({
  innerStatusWrapper: {
    flexDirection: 'column',
  },
  statusText: (props: NoteProps) => ({
    padding: theme.spacing(0, 1),
    fontWeight: 500,
    borderBottom:
      props.data.type === NoteTypes.QUALIFY
        ? `2px solid ${theme.palette.background.green}`
        : `2px solid ${Colors.MEDIUM_GRAY}`,
  }),
  caption: {
    fontWeight: 500,
    padding: theme.spacing(0, 1),
    color: theme.palette.grey['500'],
  },
  statusSvg: {
    display: 'inline-flex',
    width: theme.spacing(2.5),
    borderRadius: '50%',
    boxShadow: `0 0 2px ${Colors.LIGHT_GRAY}`,
    '& circle': {
      fill: theme.palette.background.default,
    },
  },
  statusIcon: {
    fill: Colors.MEDIUM_GRAY,
    display: 'inline-flex',
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    padding: theme.spacing(0.5),
    borderRadius: '50%',
    boxShadow: `0 0 2px ${Colors.LIGHT_GRAY}`,
  },
  consoleInnerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  consoleNote: {
    position: 'relative',
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: '5px',
    padding: theme.spacing(1.25, 3, 1.25, 1),
    '& legend': {
      color: theme.palette.primary.main,
      fontSize: 'var(--s-2)',
      fontWeight: 500,
      padding: theme.spacing(0, 1),
      textTransform: 'capitalize',
    },
  },
  consoleSvg: {
    display: 'flex',
    width: theme.spacing(4),
    borderRadius: '50%',
    backgroundColor: theme.palette.background.default,
    position: 'absolute',
    top: 'calc(50% - 0.8rem)',
    right: theme.spacing(-2),
    '& circle': {
      fill: Colors.PALE_BLUE,
    },
  },
}));

const StatusNote = ({ data, children, caption }: React.PropsWithChildren<NoteProps>) => {
  const classes = useStyles({ data, children, caption });

  let Icon = null;
  let message = '';

  if (data.type === NoteTypes.QUALIFY) {
    Icon = <QualifiedIcon className={classes.statusSvg} />;
    message = 'This lead has been qualified';
  } else if (data.type === NoteTypes.UNQUALIFY) {
    Icon = <UnqualifiedIcon className={classes.statusSvg} />;
    message = 'This lead has been unqualified';
  } else if (data.type === NoteTypes.REOPEN) {
    Icon = <RedoRoundedIcon className={classes.statusIcon} />;
    message = 'This lead has been reopened';
  }

  return (
    <>
      {Icon}
      <Box>
        <Typography variant="body2" className={classes.statusText}>
          {message}
        </Typography>
        {caption}
      </Box>
      {Icon}
    </>
  );
};

const ConsoleNote = ({ data, children, caption }: React.PropsWithChildren<NoteProps>) => {
  const classes = useStyles({ data, children, caption });
  return (
    <>
      <Box className={classes.consoleInnerWrapper}>
        <fieldset className={classes.consoleNote}>
          <NoteIcon className={classes.consoleSvg} />
          <legend>{data.type}</legend>
          {children}
        </fieldset>
      </Box>
      {caption}
    </>
  );
};

const NoteContent = ({ data, children, caption }: React.PropsWithChildren<NoteProps>) => {
  if (
    data.type === NoteTypes.QUALIFY ||
    data.type === NoteTypes.UNQUALIFY ||
    data.type === NoteTypes.REOPEN
  ) {
    return <StatusNote data={data} caption={caption} />;
  }

  return (
    <ConsoleNote data={data} caption={caption}>
      {children}
    </ConsoleNote>
  );
};

export default NoteContent;
